import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { formatDate, formatDateTime } from "@/i18n";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Inbox, Mail, MailOpen, Trash2, ChevronRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ContactMessagesAdmin() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const load = () => api.get("/contact").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openMessage = async (m) => {
    setActive(m);
    if (!m.is_read) {
      try {
        await api.put(`/contact/${m.id}/read`, { is_read: true });
        setItems((prev) => prev.map((x) => (x.id === m.id ? { ...x, is_read: true } : x)));
      } catch (_e) {
        // ignore
      }
    }
  };

  const toggleRead = async (m) => {
    try {
      const { data } = await api.put(`/contact/${m.id}/read`, { is_read: !m.is_read });
      setItems((prev) => prev.map((x) => (x.id === m.id ? data : x)));
      if (active?.id === m.id) setActive(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async () => {
    const id = confirmId;
    setConfirmId(null);
    try {
      await api.delete(`/contact/${id}`);
      toast.success(t("admin.messages.toastDeleted"));
      if (active?.id === id) setActive(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const unreadCount = items.filter((i) => !i.is_read).length;

  return (
    <div data-testid="contact-messages" className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-bordeaux-accent mb-2">{t("admin.messages.eyebrow")}</div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-4">
          {t("admin.messages.title")}
          {unreadCount > 0 && (
            <Badge className="bg-bordeaux text-beige-light rounded-sm font-medium" data-testid="unread-badge">
              {t("admin.messages.unreadBadge", { count: unreadCount })}
            </Badge>
          )}
        </h1>
        <p className="text-slate-600 mt-2">{t("admin.messages.subtitle")}</p>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className={`lg:col-span-5 bg-white border border-slate-200 rounded-sm overflow-hidden ${active ? "hidden lg:block" : ""}`}>
          {items.length === 0 ? (
            <div className="p-16 text-center text-slate-500">
              <Inbox className="mx-auto mb-3" size={22} />
              <p className="text-sm">{t("admin.messages.empty")}</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto thin-scroll">
              {items.map((m) => (
                <li key={m.id} data-testid={`message-row-${m.id}`} onClick={() => openMessage(m)}
                  className={`p-4 cursor-pointer hover:bg-beige-light transition-colors ${active?.id === m.id ? "bg-beige-light" : ""}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${m.is_read ? "bg-slate-300" : "bg-bordeaux"}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-3">
                        <span className={`text-sm truncate ${m.is_read ? "text-slate-700" : "font-semibold text-slate-900"}`}>{m.name}</span>
                        <span className="text-xs text-slate-500 tabular-nums flex-shrink-0">{formatDate(m.created_at)}</span>
                      </div>
                      <div className={`text-sm truncate mt-0.5 ${m.is_read ? "text-slate-500" : "text-slate-800"}`}>{m.subject}</div>
                      <div className="text-xs text-slate-500 truncate mt-1">{m.message}</div>
                    </div>
                    <ChevronRight size={14} className="text-slate-400 mt-1.5 flex-shrink-0" />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={`lg:col-span-7 ${!active ? "hidden lg:block" : ""}`}>
          {active ? (
            <div data-testid="message-detail" className="bg-white border border-slate-200 rounded-sm p-6 md:p-8">
              <button onClick={() => setActive(null)} className="lg:hidden inline-flex items-center gap-2 text-sm text-slate-500 hover:text-bordeaux mb-4" data-testid="message-back">
                <ArrowLeft size={14} /> {t("admin.common.back")}
              </button>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-widest text-bordeaux-accent">{formatDateTime(active.created_at)}</div>
                  <h2 className="text-2xl font-semibold text-slate-900 mt-1">{active.subject}</h2>
                  <div className="mt-3 text-sm text-slate-600">
                    <span className="font-medium text-slate-900">{active.name}</span> ·{" "}
                    <a href={`mailto:${active.email}`} className="text-bordeaux hover:underline">{active.email}</a>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleRead(active)} data-testid="message-toggle-read"
                    className="inline-flex items-center gap-2 text-sm border border-slate-300 hover:bg-beige-light rounded-sm px-3 py-1.5 text-slate-700 transition-colors">
                    {active.is_read ? <Mail size={14} /> : <MailOpen size={14} />}
                    {active.is_read ? t("admin.messages.markUnread") : t("admin.messages.markRead")}
                  </button>
                  <button onClick={() => setConfirmId(active.id)} data-testid="message-delete-btn"
                    className="inline-flex items-center gap-2 text-sm border border-slate-300 hover:bg-red-50 hover:border-red-300 hover:text-red-600 rounded-sm px-3 py-1.5 text-slate-700 transition-colors">
                    <Trash2 size={14} /> {t("admin.messages.deleteBtn")}
                  </button>
                </div>
              </div>
              <div className="h-px bg-slate-200 my-6" />
              <div data-testid="message-body" className="text-slate-700 whitespace-pre-line leading-relaxed">{active.message}</div>
              <div className="mt-8 pt-6 border-t border-slate-100">
                <a href={`mailto:${active.email}?subject=Re: ${encodeURIComponent(active.subject)}`} data-testid="message-reply-btn"
                  className="inline-flex items-center gap-2 bg-bordeaux hover:bg-bordeaux-dark text-beige-light rounded-sm px-5 py-2.5 text-sm font-medium transition-colors">
                  <Mail size={14} /> {t("admin.messages.replyBtn")}
                </a>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-dashed border-slate-200 rounded-sm p-20 text-center text-slate-500">
              <Inbox className="mx-auto mb-3" size={22} />
              <p className="text-sm">{t("admin.messages.selectMessage")}</p>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.common.confirmDeleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("admin.common.confirmDeleteDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="message-delete-cancel">{t("admin.common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="message-delete-confirm" className="bg-red-600 hover:bg-red-700">
              {t("admin.common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
